import * as React from 'react';
import classnames from 'classnames';

import { useSurvey } from '@src/hooks/useSurvey';
import { SurveyQuestionProps } from './SurveyQuestion';

export interface TypeMultiChoiceProps extends SurveyQuestionProps {}

/**
 * @name TypeMultiChoice
 * @description This component allows our user to select "#/#" options
 * from a list of options.
 */
export const TypeMultiChoice = (props: TypeMultiChoiceProps) => {
  const { question } = props;

  // Hooks
  const { answers, setAnswer, setError } = useSurvey();
  const values = answers[question.name] || [];

  // Setup
  const { choices = [], choices_max, name, properties = {} } = question;
  const { columns } = properties;

  // Handlers
  const onSelect = (key: string) => {
    const isPresent = values.includes(key);
    const newSelection = [...values];

    setError(undefined);

    if (isPresent) {
      newSelection.splice(newSelection.indexOf(key), 1);
      const newAnswer = newSelection.length !== 0 ? newSelection : undefined;

      setAnswer(name, newAnswer);
    } else {
      const isMaxed = choices_max && values.length >= choices_max;

      if (isMaxed) {
        const message = `Please select no more than ${choices_max} options.`;

        setError(message);
      } else {
        newSelection.push(key);

        setAnswer(name, newSelection);
      }
    }
  };

  return (
    <div
      className={classnames('grid gap-2', {
        'grid-cols-2 md:grid-cols-3': columns === 3,
        'md:grid-cols-2': columns === 2
      })}
    >
      {choices.map((option, index) => {
        const isSelected = values.includes(option.key);
        const onClick = () => {
          onSelect(option.key);
        };

        return (
          <div
            className={classnames(
              'cursor-pointer p-4 text-left font-beatrice-light text-16 md:px-8 md:text-18',
              {
                'bg-forest text-white': isSelected,
                'bg-white text-gray-8': !isSelected,
                'min-h-[64px] items-center': columns === 3
              }
            )}
            key={index}
            onClick={onClick}
            onKeyPress={onClick}
            tabIndex={0}
          >
            {option.value}
          </div>
        );
      })}
    </div>
  );
};
